import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Building2, CheckCircle, Clock, DollarSign, Eye, Flag, RefreshCw, ShieldAlert, TrendingUp, User, X, XCircle, } from 'lucide-react';
import { NumberedPagination } from '../pagination/NumberedPagination';
import { useTimezone } from '../timezone/TimezoneContext';
import { formatInTimezone } from '../timezone/utils';
const controlClass = 'h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-ring/30';
const severityStyles = {
    low: 'border-blue-200 bg-blue-100 text-blue-800',
    medium: 'border-amber-200 bg-amber-100 text-amber-900',
    high: 'border-orange-200 bg-orange-100 text-orange-900',
    critical: 'border-red-200 bg-red-100 text-red-900',
};
function playerLabel(p) {
    return p.username || p.email || p.phone || p.id.slice(0, 8);
}
function SummaryCard({ title, value, helper }) {
    const tone = value <= 0
        ? 'border-emerald-100 bg-emerald-50/40'
        : value >= 5
            ? 'border-red-100 bg-red-50/50'
            : 'border-amber-100 bg-amber-50/50';
    return (React.createElement("div", { className: `rounded-lg border p-3.5 shadow-sm ${tone}` },
        React.createElement("p", { className: "text-xs font-medium text-muted-foreground" }, title),
        React.createElement("p", { className: "mt-1 text-2xl font-bold tabular-nums text-foreground" }, value),
        React.createElement("p", { className: "mt-1 text-[11px] text-muted-foreground" }, helper)));
}
function statusToFilterTabId(isResolved) {
    if (isResolved === true)
        return 'resolved';
    if (isResolved === false)
        return 'open';
    return 'all';
}
function getRiskScore(detail) {
    var _a, _b, _c;
    const fromMetadata = (_b = (_a = detail.metadata) === null || _a === void 0 ? void 0 : _a.fraudRiskScore) !== null && _b !== void 0 ? _b : (_c = detail.metadata) === null || _c === void 0 ? void 0 : _c.riskScore;
    const n = Number(fromMetadata);
    if (Number.isFinite(n) && n >= 0)
        return Math.min(100, Math.round(n));
    if (detail.severity === 'critical')
        return 85;
    if (detail.severity === 'high')
        return 70;
    if (detail.severity === 'medium')
        return 45;
    return 20;
}
function getRiskTone(score) {
    if (score >= 70) {
        return { box: 'border-red-300 bg-red-50', text: 'text-red-700' };
    }
    if (score >= 40) {
        return { box: 'border-orange-300 bg-orange-50', text: 'text-orange-700' };
    }
    return { box: 'border-amber-300 bg-amber-50', text: 'text-amber-700' };
}
function isManualReviewOnly(detail) {
    var _a;
    const fromMetadata = (_a = detail.metadata) === null || _a === void 0 ? void 0 : _a.opsReviewOnly;
    if (typeof fromMetadata === 'boolean')
        return fromMetadata;
    const source = `${detail.indicatorType} ${detail.description} ${detail.reasons.join(' ')}`.toLowerCase();
    return source.includes('manual review') || source.includes('auto-approve') || source.includes('threshold');
}
function reviewSummary(detail) {
    var _a, _b, _c;
    const primary = ((_a = detail.display) === null || _a === void 0 ? void 0 : _a.reviewText) || ((_b = detail.reasons) === null || _b === void 0 ? void 0 : _b[0]) || detail.description || 'Security review required';
    const extra = Math.max(0, (((_c = detail.reasons) === null || _c === void 0 ? void 0 : _c.length) || 0) - 1);
    return extra > 0 ? `${primary} (+${extra} more)` : primary;
}
function resolveDisplayAmount(row) {
    var _a, _b;
    const fromDisplay = Number((_a = row.display) === null || _a === void 0 ? void 0 : _a.amount);
    if (Number.isFinite(fromDisplay))
        return fromDisplay;
    const direct = Number(row.amount);
    if (Number.isFinite(direct))
        return direct;
    const metadata = row.metadata;
    const candidates = [
        metadata === null || metadata === void 0 ? void 0 : metadata.amount,
        metadata === null || metadata === void 0 ? void 0 : metadata.currentRequestAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.transactionAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.requestedAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.withdrawalAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.totalWithdrawal,
        metadata === null || metadata === void 0 ? void 0 : metadata.totalOutflow,
        metadata === null || metadata === void 0 ? void 0 : metadata.withdrawAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.totalDepositAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.depositAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.spikeAmount,
        metadata === null || metadata === void 0 ? void 0 : metadata.netProfit,
        metadata === null || metadata === void 0 ? void 0 : metadata.profitAmount,
    ];
    for (const value of candidates) {
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    const firstReason = ((_b = row.reasons) === null || _b === void 0 ? void 0 : _b[0]) || row.description || '';
    const amountMatch = firstReason.match(/(?:amount|win amount|withdrawal amount)?\s*\$?\s*(-?\d+(?:\.\d+)?)/i);
    if (amountMatch === null || amountMatch === void 0 ? void 0 : amountMatch[1]) {
        const parsed = Number(amountMatch[1]);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return null;
}
function resolveDisplayDate(row) {
    var _a;
    const raw = ((_a = row.transaction) === null || _a === void 0 ? void 0 : _a.createdAt) || row.timestamp;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()))
        return '—';
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    return `${month} ${day}, ${year} ${hour}:${minute}`;
}
function resolveTypeLabel(row) {
    var _a;
    const t = ((_a = row.transaction) === null || _a === void 0 ? void 0 : _a.withdrawalType) || '';
    if (t === 'pyusd_transfer')
        return '0xProcessing payout';
    if (t === 'bitcoin_transfer')
        return 'Bitcoin payout';
    if (t === 'litecoin_transfer')
        return 'Litecoin payout';
    if (t === 'dogecoin_transfer')
        return 'Dogecoin payout';
    if (t === 'bank_transfer')
        return 'Bank transfer payout';
    return row.indicatorLabel;
}
function statusBadgeClass(status) {
    if (status === 'approved' || status === 'completed') {
        return 'border-blue-200 bg-blue-100 text-blue-700';
    }
    if (status === 'rejected' || status === 'failed') {
        return 'border-red-200 bg-red-100 text-red-700';
    }
    return 'border-yellow-200 bg-yellow-100 text-yellow-800';
}
function formatCurrencyValue(value) {
    if (value == null || Number.isNaN(Number(value)))
        return '—';
    return `$${Number(value).toFixed(2)}`;
}
export default function PlayerFraudIndicatorsPanel({ fetchList, fetchDetail, fetchWithdrawalAnalysis, resolveIndicator, showShopFilter = false, showAgentFilter = false, shopOptions = [], agentOptions = [], onError, onSuccess, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    const { timezone } = useTimezone();
    const [loading, setLoading] = useState(false);
    const [indicators, setIndicators] = useState([]);
    const [summary, setSummary] = useState(null);
    const [total, setTotal] = useState(0);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [resolveNotes, setResolveNotes] = useState('');
    const [resolving, setResolving] = useState(false);
    const [filters, setFilters] = useState({
        limit: 50,
        offset: 0,
        isResolved: false,
        sort: 'date',
        sort_dir: 'desc',
    });
    const [searchDraft, setSearchDraft] = useState('');
    const onErrorRef = useRef(onError);
    const onSuccessRef = useRef(onSuccess);
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    const handleSort = (field) => {
        setFilters((prev) => ({
            ...prev,
            sort: field,
            sort_dir: prev.sort === field && prev.sort_dir === 'desc' ? 'asc' : 'desc',
            offset: 0,
        }));
    };
    const load = useCallback(async () => {
        var _a;
        setLoading(true);
        try {
            const res = await fetchList(filters);
            if (!res.success || !res.data) {
                throw new Error(res.message || 'Failed to load Player fraud indicators');
            }
            setIndicators(res.data.indicators);
            setSummary(res.data.summary);
            setTotal(res.data.total);
        }
        catch (e) {
            const msg = (e === null || e === void 0 ? void 0 : e.message) || 'Failed to load';
            (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, msg);
            setIndicators([]);
            setSummary(null);
            setTotal(0);
        }
        finally {
            setLoading(false);
        }
    }, [fetchList, filters]);
    const currentPage = Math.floor(((_a = filters.offset) !== null && _a !== void 0 ? _a : 0) / ((_b = filters.limit) !== null && _b !== void 0 ? _b : 50)) + 1;
    const totalPages = Math.max(1, Math.ceil(total / ((_c = filters.limit) !== null && _c !== void 0 ? _c : 50)));
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        const t = window.setTimeout(() => {
            setFilters((f) => {
                const next = searchDraft.trim() || undefined;
                if (f.search === next)
                    return f;
                return { ...f, search: next, offset: 0 };
            });
        }, 350);
        return () => window.clearTimeout(t);
    }, [searchDraft]);
    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            setAnalysisData(null);
            setAnalysisLoading(false);
            setResolveNotes('');
            return;
        }
        const fromList = indicators.find((row) => row.id === selectedId);
        if (fromList) {
            setDetail(fromList);
            setDetailLoading(false);
            setResolveNotes('');
            return;
        }
        setDetailLoading(true);
        setResolveNotes('');
        fetchDetail(selectedId)
            .then((res) => {
            if (!res.success || !res.data) {
                throw new Error(res.message || 'Failed to load detail');
            }
            setDetail(res.data);
        })
            .catch((e) => {
            var _a;
            (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, (e === null || e === void 0 ? void 0 : e.message) || 'Failed to load detail');
            setDetail(null);
        })
            .finally(() => setDetailLoading(false));
    }, [selectedId, fetchDetail, indicators]);
    useEffect(() => {
        if (!(detail === null || detail === void 0 ? void 0 : detail.transactionId) || !fetchWithdrawalAnalysis) {
            setAnalysisData(null);
            setAnalysisLoading(false);
            return;
        }
        setAnalysisLoading(true);
        fetchWithdrawalAnalysis(detail.transactionId)
            .then((res) => {
            if (res.success && res.data) {
                setAnalysisData(res.data);
            }
            else {
                setAnalysisData(null);
            }
        })
            .catch(() => setAnalysisData(null))
            .finally(() => setAnalysisLoading(false));
    }, [detail === null || detail === void 0 ? void 0 : detail.transactionId, fetchWithdrawalAnalysis]);
    const handleResolve = async () => {
        var _a, _b;
        if (!selectedId || !resolveIndicator || !detail || detail.isResolved)
            return;
        setResolving(true);
        try {
            const res = await resolveIndicator(selectedId, resolveNotes.trim() || undefined);
            if (!res.success || !res.data) {
                throw new Error(res.message || 'Failed to resolve indicator');
            }
            setDetail(res.data);
            (_a = onSuccessRef.current) === null || _a === void 0 ? void 0 : _a.call(onSuccessRef, 'Indicator marked as resolved');
            void load();
        }
        catch (e) {
            (_b = onErrorRef.current) === null || _b === void 0 ? void 0 : _b.call(onErrorRef, (e === null || e === void 0 ? void 0 : e.message) || 'Failed to resolve');
        }
        finally {
            setResolving(false);
        }
    };
    const jumpToPage = (page) => {
        var _a;
        const limit = (_a = filters.limit) !== null && _a !== void 0 ? _a : 50;
        const clamped = Math.min(Math.max(1, page), totalPages);
        setFilters((prev) => ({ ...prev, offset: (clamped - 1) * limit }));
    };
    const summaryCards = useMemo(() => {
        var _a, _b, _c, _d, _e;
        return [
            { title: 'High-risk Players', value: (_a = summary === null || summary === void 0 ? void 0 : summary.highRiskPlayers) !== null && _a !== void 0 ? _a : 0, helper: 'Risk score ≥ 70' },
            { title: 'Unusual game winnings', value: (_b = summary === null || summary === void 0 ? void 0 : summary.unusualGameWinnings) !== null && _b !== void 0 ? _b : 0, helper: 'Game wallet flags' },
            { title: 'New Player withdrawal attempt', value: (_c = summary === null || summary === void 0 ? void 0 : summary.newPlayerWithdrawalAttempt) !== null && _c !== void 0 ? _c : 0, helper: 'New account, high profit' },
            { title: 'Player inactivity spike', value: (_d = summary === null || summary === void 0 ? void 0 : summary.playerInactivitySpike) !== null && _d !== void 0 ? _d : 0, helper: 'Inactive then active' },
            { title: 'Other transaction flags', value: (_e = summary === null || summary === void 0 ? void 0 : summary.otherTransactionFlags) !== null && _e !== void 0 ? _e : 0, helper: 'Deposit / profit flags' },
        ];
    }, [summary]);
    const modalAnalysis = (analysisData || {});
    const modalProfit = (modalAnalysis.profitAnalysis || {});
    const modalAlert = (modalAnalysis.alertSummary || {});
    const modalOpsMessages = modalAnalysis.opsReviewMessages || modalAlert.opsReviewMessages || [];
    const modalFraudMessages = modalAnalysis.fraudAlertMessages || modalAlert.fraudAlertMessages || [];
    const modalHasFraudSignals = Boolean((_e = (_d = modalAnalysis.hasFraudSignals) !== null && _d !== void 0 ? _d : modalAlert.hasFraudSignals) !== null && _e !== void 0 ? _e : modalFraudMessages.length > 0);
    const modalOpsOnly = Boolean((_g = (_f = modalAnalysis.opsReviewOnly) !== null && _f !== void 0 ? _f : modalAlert.opsReviewOnly) !== null && _g !== void 0 ? _g : (modalOpsMessages.length > 0 && !modalHasFraudSignals));
    const modalRiskScore = Number((_l = (_k = (_j = (_h = modalAnalysis.fraudRiskScore) !== null && _h !== void 0 ? _h : modalAlert.fraudRiskScore) !== null && _j !== void 0 ? _j : modalAnalysis.riskScore) !== null && _k !== void 0 ? _k : modalAlert.riskScore) !== null && _l !== void 0 ? _l : 0);
    return (React.createElement("div", { className: "space-y-4" },
        React.createElement("div", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" }, summaryCards.map((c) => (React.createElement(SummaryCard, { key: c.title, ...c })))),
        React.createElement("div", { className: "rounded-lg border bg-card shadow-sm" },
            React.createElement("div", { className: "border-b p-4" },
                React.createElement("div", { className: "flex items-center justify-between gap-3" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(AlertTriangle, { className: "h-5 w-5 text-orange-500" }),
                        React.createElement("h2", { className: "text-lg font-semibold text-foreground" },
                            "Fraud indicators",
                            total > 0 ? (React.createElement("span", { className: "ml-2 text-sm font-normal text-muted-foreground" },
                                "(",
                                total,
                                " total)")) : null)),
                    React.createElement("button", { type: "button", onClick: () => void load(), disabled: loading, className: "inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-60" },
                        React.createElement(RefreshCw, { className: `h-4 w-4 ${loading ? 'animate-spin' : ''}` }),
                        "Refresh")),
                React.createElement("div", { className: "mt-4" },
                    React.createElement("div", { className: "inline-flex rounded-lg border border-input bg-muted/30 p-1" }, ['open', 'resolved', 'all'].map((tab) => {
                        const active = statusToFilterTabId(filters.isResolved) === tab;
                        const label = tab === 'open' ? 'Open' : tab === 'resolved' ? 'Resolved' : 'All';
                        return (React.createElement("button", { key: tab, type: "button", onClick: () => setFilters((f) => ({
                                ...f,
                                isResolved: tab === 'open' ? false : tab === 'resolved' ? true : undefined,
                                offset: 0,
                            })), className: `rounded-md px-3 py-1.5 text-sm font-medium transition ${active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}` }, label));
                    }))),
                React.createElement("div", { className: "mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-12 xl:items-end" },
                    React.createElement("div", { className: "xl:col-span-3" },
                        React.createElement("label", { className: "mb-1 block text-xs font-medium text-muted-foreground" }, "Search Players"),
                        React.createElement("input", { className: controlClass, placeholder: "Username, email, phone", value: searchDraft, onChange: (e) => setSearchDraft(e.target.value) })),
                    React.createElement("div", { className: "xl:col-span-2" },
                        React.createElement("label", { className: "mb-1 block text-xs font-medium text-muted-foreground" }, "Severity"),
                        React.createElement("select", { className: controlClass, value: filters.severity || '', onChange: (e) => setFilters((f) => ({ ...f, severity: e.target.value || undefined, offset: 0 })) },
                            React.createElement("option", { value: "" }, "All severities"),
                            React.createElement("option", { value: "low" }, "Low"),
                            React.createElement("option", { value: "medium" }, "Medium"),
                            React.createElement("option", { value: "high" }, "High"),
                            React.createElement("option", { value: "critical" }, "Critical"))),
                    showAgentFilter ? (React.createElement("div", { className: "xl:col-span-2" },
                        React.createElement("label", { className: "mb-1 block text-xs font-medium text-muted-foreground" }, "Agent"),
                        React.createElement("select", { className: controlClass, value: filters.agentId || '', onChange: (e) => setFilters((f) => ({ ...f, agentId: e.target.value || undefined, offset: 0 })) },
                            React.createElement("option", { value: "" }, "All Agents"),
                            agentOptions.map((a) => (React.createElement("option", { key: a.id, value: a.id }, a.name)))))) : null,
                    showShopFilter ? (React.createElement("div", { className: "xl:col-span-2" },
                        React.createElement("label", { className: "mb-1 block text-xs font-medium text-muted-foreground" }, "Shop"),
                        React.createElement("select", { className: controlClass, value: filters.shopId || '', onChange: (e) => setFilters((f) => ({ ...f, shopId: e.target.value || undefined, offset: 0 })) },
                            React.createElement("option", { value: "" }, "All Shops"),
                            shopOptions.map((s) => (React.createElement("option", { key: s.id, value: s.id }, s.name)))))) : null,
                    React.createElement("div", { className: "xl:col-span-3" },
                        React.createElement("label", { className: "mb-1 block text-xs font-medium text-muted-foreground" }, "Page size"),
                        React.createElement("select", { className: controlClass, value: filters.limit || 50, onChange: (e) => setFilters((f) => ({ ...f, limit: Number(e.target.value), offset: 0 })) },
                            React.createElement("option", { value: 25 }, "25"),
                            React.createElement("option", { value: 50 }, "50"),
                            React.createElement("option", { value: 100 }, "100")))))),
        React.createElement("div", { className: "overflow-hidden rounded-lg border bg-card shadow-sm" },
            loading ? (React.createElement("div", { className: "py-14 text-center text-sm text-muted-foreground" }, "Loading Player indicators\u2026")) : indicators.length === 0 ? (React.createElement("div", { className: "flex flex-col items-center py-16 text-center" },
                React.createElement(ShieldAlert, { className: "mb-2 h-9 w-9 text-muted-foreground/50" }),
                React.createElement("p", { className: "text-sm font-semibold text-foreground" }, "No Player fraud indicators"),
                React.createElement("p", { className: "mt-1 text-xs text-muted-foreground" }, "Try another filter or status tab."))) : (React.createElement("div", { className: "overflow-x-auto" },
                React.createElement("table", { className: "w-full min-w-[980px] text-sm" },
                    React.createElement("thead", null,
                        React.createElement("tr", { className: "border-b" },
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" }, "Transaction"),
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" }, "Player"),
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" }, "Type"),
                            React.createElement("th", { className: "px-3 py-3 text-right font-medium text-muted-foreground" },
                                React.createElement("button", { type: "button", onClick: () => handleSort('amount'), className: "inline-flex w-full items-center justify-end gap-1 hover:text-foreground", "aria-label": "Sort by Amount" },
                                    "Amount",
                                    React.createElement("span", { className: "text-[10px] opacity-70", "aria-hidden": true }, filters.sort === 'amount' ? (filters.sort_dir === 'asc' ? '↑' : '↓') : '↕'))),
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" }, "Status"),
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" }, "Review"),
                            React.createElement("th", { className: "px-3 py-3 text-left font-medium text-muted-foreground" },
                                React.createElement("button", { type: "button", onClick: () => handleSort('date'), className: "inline-flex items-center gap-1 hover:text-foreground", "aria-label": "Sort by Date" },
                                    "Date",
                                    React.createElement("span", { className: "text-[10px] opacity-70", "aria-hidden": true }, filters.sort === 'date' ? (filters.sort_dir === 'asc' ? '↑' : '↓') : '↕'))),
                            React.createElement("th", { className: "px-3 py-3 text-right font-medium text-muted-foreground" }, "Actions"))),
                    React.createElement("tbody", null, indicators.map((row) => {
                        var _a, _b, _c, _d;
                        const displayAmount = resolveDisplayAmount(row);
                        const txType = ((_a = row.transaction) === null || _a === void 0 ? void 0 : _a.type) || 'withdrawal';
                        const signedAmount = txType === 'withdrawal' && displayAmount != null ? -Math.abs(displayAmount) : displayAmount;
                        const effectiveStatus = ((_b = row.display) === null || _b === void 0 ? void 0 : _b.status) || ((_c = row.transaction) === null || _c === void 0 ? void 0 : _c.status) || (row.isResolved ? 'approved' : 'awaiting_admin');
                        return (React.createElement("tr", { key: row.id, className: "cursor-pointer border-b last:border-0 hover:bg-muted/40 transition-colors", onClick: () => setSelectedId(row.id) },
                            React.createElement("td", { className: "px-3 py-3" },
                                React.createElement("div", { className: "font-mono text-xs text-foreground" }, row.transactionId || '—')),
                            React.createElement("td", { className: "px-3 py-3" },
                                React.createElement("div", { className: "font-medium text-foreground" }, playerLabel(row.player)),
                                React.createElement("div", { className: "text-xs text-muted-foreground" },
                                    row.player.email || row.player.phone || row.player.id,
                                    showShopFilter ? ` · ${row.shop.name}` : '',
                                    showAgentFilter && ((_d = row.agent) === null || _d === void 0 ? void 0 : _d.name) ? ` · ${row.agent.name}` : '')),
                            React.createElement("td", { className: "px-3 py-3" },
                                React.createElement("span", { className: "inline-flex rounded-full border bg-muted px-3 py-1 text-xs font-medium text-foreground" }, resolveTypeLabel(row))),
                            React.createElement("td", { className: `px-3 py-3 text-right font-mono font-semibold ${signedAmount != null && signedAmount < 0 ? 'text-red-600' : 'text-foreground'}` }, signedAmount != null ? `${signedAmount < 0 ? '-' : ''}$${Math.abs(signedAmount).toFixed(2)}` : '—'),
                            React.createElement("td", { className: "px-3 py-3" },
                                React.createElement("span", { className: `inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(effectiveStatus)}` }, effectiveStatus)),
                            React.createElement("td", { className: "px-3 py-3 text-sm text-muted-foreground" }, reviewSummary(row)),
                            React.createElement("td", { className: "px-3 py-3 text-xs text-muted-foreground" }, resolveDisplayDate(row)),
                            React.createElement("td", { className: "px-3 py-3", onClick: (e) => e.stopPropagation() },
                                React.createElement("div", { className: "flex justify-end gap-1" },
                                    React.createElement("button", { type: "button", className: "inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted", onClick: () => setSelectedId(row.id), title: "View indicator detail" },
                                        React.createElement(Eye, { className: "h-4 w-4" }))))));
                    }))))),
            totalPages > 1 ? (React.createElement("div", { className: "flex items-center justify-between border-t px-3 py-3" },
                React.createElement("div", { className: "text-sm text-muted-foreground" },
                    "Page ",
                    currentPage,
                    " of ",
                    totalPages,
                    " (",
                    total,
                    " items)"),
                React.createElement(NumberedPagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: jumpToPage, disabled: loading, variant: "admin" }))) : (React.createElement("div", { className: "border-t px-3 py-2 text-xs text-muted-foreground" },
                "Showing ",
                indicators.length,
                " of ",
                total,
                " indicators"))),
        selectedId ? (React.createElement("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50", role: "presentation", onClick: () => setSelectedId(null) },
            React.createElement("div", { className: "max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card shadow-xl", role: "dialog", "aria-modal": "true", onClick: (e) => e.stopPropagation() },
                React.createElement("div", { className: "sticky top-0 z-10 flex items-center justify-between border-b bg-card px-4 py-3" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(ShieldAlert, { className: "h-5 w-5 text-orange-500" }),
                        React.createElement("h3", { className: "text-lg font-semibold text-foreground" }, detail && (modalOpsOnly || isManualReviewOnly(detail)) ? 'Withdrawal review' : 'Withdrawal review & fraud signals')),
                    React.createElement("button", { type: "button", onClick: () => setSelectedId(null), className: "rounded-md p-1 text-muted-foreground hover:bg-muted" },
                        React.createElement(X, { className: "h-5 w-5" }))),
                detailLoading || !detail ? (React.createElement("div", { className: "p-6 text-sm text-muted-foreground" }, "Loading\u2026")) : (React.createElement("div", { className: "space-y-5 p-4 text-sm" },
                    analysisLoading ? (React.createElement("section", { className: "rounded-lg border p-4 text-muted-foreground" }, "Loading analysis\u2026")) : null,
                    !analysisLoading && (modalOpsMessages.length > 0 || isManualReviewOnly(detail)) ? (React.createElement("section", { className: "rounded-lg border-2 border-blue-200 bg-blue-50/80 p-4" },
                        React.createElement("div", { className: "mb-2 flex items-center justify-between gap-2" },
                            React.createElement("p", { className: "text-sm font-semibold uppercase tracking-wide text-blue-900" }, "Manual review required"),
                            React.createElement("span", { className: "rounded border border-blue-300 bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-900" }, "Operations check, not a fraud score")),
                        React.createElement("p", { className: "text-sm font-medium text-blue-950" }, "Requires review because:"),
                        React.createElement("ul", { className: "mt-2 space-y-1.5" }, (modalOpsMessages.length > 0 ? modalOpsMessages : detail.reasons).map((r) => (React.createElement("li", { key: r, className: "flex items-start gap-2 text-blue-950" },
                            React.createElement("span", { className: "mt-1 inline-block h-1.5 w-1.5 rounded-full bg-blue-600" }),
                            React.createElement("span", null, r))))))) : null,
                    React.createElement("section", { className: `rounded-lg border-2 p-4 ${getRiskTone(modalRiskScore || getRiskScore(detail)).box}` },
                        React.createElement("div", { className: "mb-2 flex items-center justify-between gap-2" },
                            React.createElement("p", { className: "text-sm font-semibold uppercase tracking-wide text-gray-700" }, "Fraud signals"),
                            React.createElement("span", { className: `text-xl font-bold ${getRiskTone(modalRiskScore || getRiskScore(detail)).text}` },
                                "Fraud risk score: ",
                                (modalRiskScore || getRiskScore(detail)),
                                "/100")),
                        React.createElement("p", { className: "text-sm font-medium text-gray-800" }, "Flagged because:"),
                        React.createElement("ul", { className: "mt-2 space-y-1.5" }, (modalFraudMessages.length > 0 ? modalFraudMessages : detail.reasons).map((r) => (React.createElement("li", { key: r, className: "flex items-start gap-2 text-gray-900" },
                            React.createElement("span", { className: "mt-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500" }),
                            React.createElement("span", null, r)))))),
                    React.createElement("section", { className: "grid grid-cols-2 gap-3" },
                        React.createElement("div", { className: "rounded-lg border p-4" },
                            React.createElement("div", { className: "mb-1 flex items-center gap-2" },
                                React.createElement(TrendingUp, { className: "h-4 w-4" }),
                                React.createElement("p", { className: "text-sm font-medium" }, "Risk score")),
                            React.createElement("p", { className: "text-2xl font-bold" },
                                (modalRiskScore || getRiskScore(detail)),
                                "/100"),
                            React.createElement("p", { className: "mt-1 text-xs text-muted-foreground" }, "Fraud risk scoring")),
                        React.createElement("div", { className: "rounded-lg border p-4" },
                            React.createElement("div", { className: "mb-1 flex items-center gap-2" },
                                React.createElement(DollarSign, { className: "h-4 w-4" }),
                                React.createElement("p", { className: "text-sm font-medium" }, "Net profit")),
                            React.createElement("p", { className: "text-2xl font-bold" }, formatCurrencyValue(Number((_p = (_m = modalProfit.netProfit) !== null && _m !== void 0 ? _m : (_o = detail.metadata) === null || _o === void 0 ? void 0 : _o.netProfit) !== null && _p !== void 0 ? _p : resolveDisplayAmount(detail)))),
                            React.createElement("p", { className: "mt-1 text-xs text-muted-foreground" }, "Derived from fraud analysis metadata"))),
                    React.createElement("section", { className: "rounded-lg border p-4" },
                        React.createElement("h4", { className: "mb-3 flex items-center gap-2 text-sm font-semibold" },
                            React.createElement(DollarSign, { className: "h-4 w-4" }),
                            "Financial breakdown"),
                        React.createElement("div", { className: "space-y-2 text-sm" },
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Total deposits"),
                                React.createElement("span", { className: "font-medium" }, formatCurrencyValue(Number((_q = modalProfit.totalDeposits) !== null && _q !== void 0 ? _q : (_r = detail.metadata) === null || _r === void 0 ? void 0 : _r.totalDeposits)))),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Completed withdrawals"),
                                React.createElement("span", { className: "font-medium" }, formatCurrencyValue(Number((_s = modalProfit.totalWithdrawals) !== null && _s !== void 0 ? _s : (_t = detail.metadata) === null || _t === void 0 ? void 0 : _t.totalWithdrawals)))),
                            React.createElement("div", { className: "flex justify-between border-t pt-2" },
                                React.createElement("span", { className: "text-muted-foreground font-medium" }, "This request"),
                                React.createElement("span", { className: "font-bold" }, formatCurrencyValue(Number((_u = modalProfit.currentRequestAmount) !== null && _u !== void 0 ? _u : resolveDisplayAmount(detail))))))),
                    React.createElement("section", { className: "rounded-lg border p-4" },
                        React.createElement("h4", { className: "mb-3 flex items-center gap-2 text-sm font-semibold" },
                            React.createElement(User, { className: "h-4 w-4" }),
                            "Player info"),
                        React.createElement("div", { className: "space-y-2 text-sm" },
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Username"),
                                React.createElement("span", { className: "font-medium" }, playerLabel(detail.player))),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Email"),
                                React.createElement("span", { className: "font-medium" }, detail.player.email || '—')),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Status"),
                                React.createElement("span", { className: "font-medium" }, detail.player.status)))),
                    React.createElement("section", { className: "rounded-lg border p-4" },
                        React.createElement("h4", { className: "mb-3 flex items-center gap-2 text-sm font-semibold" },
                            React.createElement(Building2, { className: "h-4 w-4" }),
                            "Network"),
                        React.createElement("div", { className: "space-y-2 text-sm" },
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Shop"),
                                React.createElement("span", { className: "font-medium" }, detail.shop.name)),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Agent"),
                                React.createElement("span", { className: "font-medium" }, ((_v = detail.agent) === null || _v === void 0 ? void 0 : _v.name) || '—')))),
                    React.createElement("section", { className: "rounded-lg border p-4" },
                        React.createElement("h4", { className: "mb-3 flex items-center gap-2 text-sm font-semibold" },
                            React.createElement(Flag, { className: "h-4 w-4" }),
                            "Fraud flags (1)"),
                        React.createElement("div", { className: "rounded-md border bg-muted/30 p-3" },
                            React.createElement("div", { className: "text-sm font-medium" }, detail.indicatorLabel),
                            React.createElement("div", { className: "mt-1 text-xs text-muted-foreground" }, detail.description))),
                    React.createElement("section", { className: "rounded-lg border p-4 text-xs" },
                        React.createElement("h4", { className: "mb-3 flex items-center gap-2 text-sm font-semibold" },
                            React.createElement(Clock, { className: "h-4 w-4" }),
                            "Transaction"),
                        React.createElement("div", { className: "space-y-2" },
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Transaction ID"),
                                React.createElement("span", { className: "font-mono text-[11px]" }, detail.transactionId)),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Amount"),
                                React.createElement("span", { className: "font-bold" }, formatCurrencyValue(resolveDisplayAmount(detail)))),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Status"),
                                React.createElement("span", null, String(((_w = modalAnalysis === null || modalAnalysis === void 0 ? void 0 : modalAnalysis.transaction) === null || _w === void 0 ? void 0 : _w.status) || ((_x = detail.display) === null || _x === void 0 ? void 0 : _x.status) || ((_y = detail.transaction) === null || _y === void 0 ? void 0 : _y.status) || 'awaiting_admin'))),
                            React.createElement("div", { className: "flex justify-between" },
                                React.createElement("span", { className: "text-muted-foreground" }, "Created"),
                                React.createElement("span", null, resolveDisplayDate(detail))))),
                    detail.metadata && Object.keys(detail.metadata).length > 0 ? (React.createElement("section", { className: "rounded-lg border p-4" },
                        React.createElement("h4", { className: "mb-2 text-sm font-semibold" }, "Notes"),
                        React.createElement("p", { className: "text-xs text-muted-foreground" }, reviewSummary(detail)))) : null,
                    React.createElement("section", { className: "grid grid-cols-2 gap-2 rounded-lg border p-4 text-xs" },
                        React.createElement("div", null,
                            React.createElement("span", { className: "text-muted-foreground" }, "Amount"),
                            React.createElement("p", { className: "font-mono font-semibold" }, detail.amount != null ? `$${detail.amount}` : '—')),
                        React.createElement("div", null,
                            React.createElement("span", { className: "text-muted-foreground" }, "Severity"),
                            React.createElement("p", { className: "font-semibold capitalize" }, detail.severity)),
                        React.createElement("div", { className: "col-span-2" },
                            React.createElement("span", { className: "text-muted-foreground" }, "Timestamp"),
                            React.createElement("p", null, formatInTimezone(detail.timestamp, timezone))),
                        React.createElement("div", { className: "col-span-2" },
                            React.createElement("span", { className: "text-muted-foreground" }, "Source"),
                            React.createElement("p", null,
                                detail.sourceType,
                                detail.sourceId ? ` · ${detail.sourceId}` : ''))),
                    detail.isResolved ? (React.createElement("section", { className: "rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" },
                        React.createElement("p", { className: "font-semibold" }, "Resolved"),
                        detail.resolvedAt ? (React.createElement("p", { className: "mt-1 text-emerald-800" }, formatInTimezone(detail.resolvedAt, timezone))) : null,
                        detail.resolutionNotes ? (React.createElement("p", { className: "mt-1 text-emerald-800" }, detail.resolutionNotes)) : null)) : resolveIndicator ? (React.createElement("section", { className: "space-y-3 border-t pt-4" },
                        React.createElement("h4", { className: "text-sm font-semibold" }, "Resolve indicator"),
                        React.createElement("textarea", { className: "min-h-[84px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/30", placeholder: "Optional notes (e.g. reviewed with the Player)", value: resolveNotes, onChange: (e) => setResolveNotes(e.target.value), disabled: resolving }),
                        React.createElement("div", { className: "flex gap-2" },
                            React.createElement("button", { type: "button", onClick: () => void handleResolve(), disabled: resolving, className: "inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-green-600 px-3 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60" },
                                React.createElement(CheckCircle, { className: "h-4 w-4" }),
                                resolving ? 'Resolving…' : 'Mark as resolved'),
                            React.createElement("button", { type: "button", onClick: () => setSelectedId(null), className: "inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground hover:bg-muted/40" },
                                React.createElement(XCircle, { className: "h-4 w-4" }),
                                "Close")))) : null))))) : null));
}
